import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { appKeys } from '@/api/query-keys';
import {
  beginListWrite,
  beginRowWrite,
  endListWrite,
  endRowWrite,
  patchRow,
  type RowWrite
} from '@/api/queries/optimisticRowWrite';
import { useAuthStore } from '@/stores/auth-store';
import i18next from '@/lib/i18n';
import {
  superadminListInstalledAppsContract,
  superadminUnlockAppContract,
  superadminLockAppContract,
  superadminSetAppSeatLimitContract,
  superadminSetAppConfigContract,
  superadminSetAppAllowedContract,
  type ListInstalledAppsResponse,
  type ActivateAppResponse,
  type LockAppResponse,
  type SetAppAllowedRequest,
  type SetAppAllowedResponse,
  type SetAppConfigRequest,
  type SetAppConfigResponse,
  type SetAppSeatLimitRequest,
  type SetAppSeatLimitResponse
} from '@bliks-io/contracts';

type TenantApp = ListInstalledAppsResponse['apps'][number];

function tenantAppRow(tenantId: string, appId: string): Omit<RowWrite<TenantApp>, 'previous'> {
  return {
    listKey: appKeys.tenantApps(tenantId),
    appId
  };
}

export function useTenantApps(tenantId: string) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: appKeys.tenantApps(tenantId),
    queryFn: async ({ signal }) => {
      const path = superadminListInstalledAppsContract.path.replace(':tenantId', tenantId);
      const { data } = await apiClient.get<ListInstalledAppsResponse>(path, {
        signal
      });
      return data.apps;
    },
    enabled: !!token && !!tenantId
  });
}

export function useUnlockTenantApp(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (appId: string) => {
      const path = superadminUnlockAppContract.path
        .replace(':tenantId', tenantId)
        .replace(':appId', appId);
      const { data } = await apiClient.post<ActivateAppResponse>(path, {});
      return data;
    },
    onMutate: () => beginListWrite(queryClient, appKeys.tenantApps(tenantId)),
    onSuccess: (_data, _appId, write) => {
      toast.success(i18next.t('adminSettings.tenant.addAppSuccess'));
      return endListWrite(queryClient, write);
    },
    onError: (_error, _appId, write) => {
      void endListWrite(queryClient, write);
      toast.error(i18next.t('adminSettings.tenant.addAppFailed'));
    }
  });
}

export function useSetTenantAppSeatLimit(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appId, seatLimit }: { appId: string } & SetAppSeatLimitRequest) => {
      const path = superadminSetAppSeatLimitContract.path
        .replace(':tenantId', tenantId)
        .replace(':appId', appId);
      const { data } = await apiClient.post<SetAppSeatLimitResponse>(path, { seatLimit });
      return data;
    },
    onMutate: () => beginListWrite(queryClient, appKeys.tenantApps(tenantId)),
    onSuccess: (response, { appId }, write) => {
      toast.success(i18next.t('adminSettings.tenant.seatLimitSaved'));
      // The re-read may wait for a toggle still in flight.
      patchRow<TenantApp>(queryClient, tenantAppRow(tenantId, appId), {
        seatLimit: response.app.seatLimit
      });
      void endListWrite(queryClient, write);
    },
    onError: (_error, _variables, write) => {
      void endListWrite(queryClient, write);
      toast.error(i18next.t('adminSettings.tenant.seatLimitSaveFailed'));
    }
  });
}

/**
 * The tenant's permission to use the app. Withdrawing it keeps the subdoc, so
 * seats and config survive — only removing the app discards them.
 */
export function useSetTenantAppAllowed(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: appKeys.tenantAppWriteMutations(tenantId),
    mutationFn: async ({ appId, allowed }: { appId: string } & SetAppAllowedRequest) => {
      const path = superadminSetAppAllowedContract.path
        .replace(':tenantId', tenantId)
        .replace(':appId', appId);
      const { data } = await apiClient.put<SetAppAllowedResponse>(path, { allowed });
      return data;
    },
    onMutate: ({ appId, allowed }) =>
      beginRowWrite<TenantApp>(queryClient, tenantAppRow(tenantId, appId), { allowed }),
    onSuccess: (_data, _variables, write) => {
      toast.success(i18next.t('adminSettings.tenant.appAllowedSaved'));
      return endRowWrite(queryClient, write, null);
    },
    onError: (error, _variables, write) => {
      void endRowWrite(queryClient, write, error);
      toast.error(i18next.t('adminSettings.tenant.appAllowedSaveFailed'));
    }
  });
}

export function useSetTenantAppConfig(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: appKeys.tenantAppWriteMutations(tenantId),
    mutationFn: async ({ appId, config }: { appId: string } & SetAppConfigRequest) => {
      const path = superadminSetAppConfigContract.path
        .replace(':tenantId', tenantId)
        .replace(':appId', appId);
      const { data } = await apiClient.put<SetAppConfigResponse>(path, { config });
      return data;
    },
    onMutate: ({ appId, config }) =>
      beginRowWrite<TenantApp>(queryClient, tenantAppRow(tenantId, appId), { config }),
    onSuccess: (_data, _variables, write) => {
      toast.success(i18next.t('adminSettings.tenant.appConfigSaved'));
      return endRowWrite(queryClient, write, null);
    },
    onError: (error, _variables, write) => {
      void endRowWrite(queryClient, write, error);
      // The registry rejections name what is wrong with the submitted config
      // (`errors.appConfig.*`); a generic toast would leave the superadmin with
      // a form they cannot tell apart from a network failure.
      toastApiError(error, 'adminSettings.tenant.appConfigSaveFailed');
    }
  });
}

export function useLockTenantApp(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: appKeys.tenantAppLockMutations(tenantId),
    mutationFn: async (appId: string) => {
      const path = superadminLockAppContract.path
        .replace(':tenantId', tenantId)
        .replace(':appId', appId);
      const { data } = await apiClient.post<LockAppResponse>(path, {});
      return data;
    },
    onMutate: () => beginListWrite(queryClient, appKeys.tenantApps(tenantId)),
    onSuccess: (_data, _appId, write) => {
      toast.success(i18next.t('adminSettings.tenant.removeAppSuccess'));
      return endListWrite(queryClient, write);
    },
    onError: (_error, _appId, write) => {
      void endListWrite(queryClient, write);
      toast.error(i18next.t('adminSettings.tenant.removeAppFailed'));
    }
  });
}
